import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, errMsg, formatIDR } from "@/lib/api";

const REQUIRED_COLS = ["name"];
const ALL_COLS = ["name", "sku", "unit", "pack_size", "stock", "purchase_price", "retail_price"];

function normalizeRow(row) {
  const r = {};
  Object.keys(row).forEach((k) => {
    const key = k.toString().trim().toLowerCase().replace(/\s+/g, "_");
    // Map common Indonesian headers
    const map = {
      "nama": "name", "produk": "name", "nama_produk": "name",
      "kode": "sku",
      "satuan": "unit",
      "isi_karton": "pack_size", "isi/karton": "pack_size", "pack_size": "pack_size", "karton": "pack_size",
      "stok": "stock", "stok_awal": "stock",
      "harga_beli": "purchase_price", "beli": "purchase_price",
      "harga_jual": "retail_price", "jual": "retail_price",
    };
    r[map[key] || key] = row[k];
  });
  return {
    name: (r.name || "").toString().trim(),
    sku: (r.sku || "").toString().trim(),
    unit: (r.unit || "pcs").toString().trim(),
    pack_size: Number(r.pack_size) || 1,
    stock: Number(r.stock) || 0,
    purchase_price: Number(r.purchase_price) || 0,
    retail_price: Number(r.retail_price) || 0,
  };
}

export default function ImportProductsDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("upsert");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseFile = (file) => {
    setResult(null);
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "csv" || ext === "txt") {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: (res) => {
          const data = res.data.map(normalizeRow).filter((r) => r.name);
          setRows(data);
          if (data.length === 0) toast.error("Tidak ada baris valid ditemukan");
          else toast.success(`${data.length} baris siap diimpor`);
        },
        error: (err) => toast.error(`Gagal parse: ${err.message}`),
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(ws);
          const data = json.map(normalizeRow).filter((r) => r.name);
          setRows(data);
          if (data.length === 0) toast.error("Tidak ada baris valid ditemukan");
          else toast.success(`${data.length} baris siap diimpor`);
        } catch (err) { toast.error(`Gagal baca Excel: ${err.message}`); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      toast.error("Format tidak didukung. Gunakan .csv atau .xlsx");
    }
  };

  const submit = async () => {
    if (rows.length === 0) return toast.error("Belum ada data untuk diimpor");
    setBusy(true);
    try {
      const { data } = await api.post("/products/bulk-import", { items: rows, mode });
      setResult(data);
      toast.success(`Impor selesai: ${data.created} baru, ${data.updated} update, ${data.skipped} lewat`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const sample = [
      { name: "Kopi Susu 250ml", sku: "KS-250", unit: "botol", pack_size: 12, stock: 0, purchase_price: 10000, retail_price: 15000 },
      { name: "Teh Melati 500ml", sku: "TM-500", unit: "botol", pack_size: 24, stock: 0, purchase_price: 8000, retail_price: 12000 },
    ];
    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Produk");
    XLSX.writeFile(wb, "Template-Impor-Produk-Hotto.xlsx");
  };

  const close = () => { setRows([]); setResult(null); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5"/>Impor Produk Massal</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <Card className="p-4 border border-dashed border-[var(--hotto-border)] bg-slate-50/50">
            <div className="flex items-start justify-between gap-4">
              <div className="text-sm text-slate-600">
                <b>Format kolom</b> (baris pertama = header):
                <div className="mt-1 flex flex-wrap gap-1">
                  {ALL_COLS.map((c) => (
                    <Badge key={c} variant="outline" className="text-xs font-mono-num">{c}{REQUIRED_COLS.includes(c) && <span className="text-red-500 ml-0.5">*</span>}</Badge>
                  ))}
                </div>
                <div className="text-xs text-slate-500 mt-2">Bisa juga pakai nama Indonesia: nama, kode, satuan, isi_karton, stok, harga_beli, harga_jual</div>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="btn-download-template">
                <Download className="w-4 h-4 mr-2"/> Template Excel
              </Button>
            </div>
          </Card>

          <label className="block cursor-pointer border-2 border-dashed border-[var(--hotto-border)] rounded-md p-8 text-center hover:border-[var(--hotto-primary)] hover:bg-emerald-50/30 transition-colors">
            <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={(e)=>e.target.files[0] && parseFile(e.target.files[0])} data-testid="input-import-file"/>
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2"/>
            <div className="text-sm font-medium">Klik untuk pilih file CSV atau Excel</div>
            <div className="text-xs text-slate-500 mt-1">.csv, .xlsx, .xls (max 5000 baris)</div>
          </label>

          <div>
            <Label className="text-sm">Jika produk sudah ada (SKU/Nama sama)</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="upsert" id="mode-upsert" data-testid="mode-upsert"/>
                <Label htmlFor="mode-upsert" className="text-sm font-normal cursor-pointer">Update data + tambah stok</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="skip_existing" id="mode-skip" data-testid="mode-skip"/>
                <Label htmlFor="mode-skip" className="text-sm font-normal cursor-pointer">Lewati (hanya impor produk baru)</Label>
              </div>
            </RadioGroup>
          </div>

          {rows.length > 0 && (
            <div>
              <div className="overline mb-2">Preview ({rows.length} baris)</div>
              <div className="border border-[var(--hotto-border)] rounded-md overflow-auto max-h-72">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 sticky top-0">
                      <TableHead>Nama</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Satuan</TableHead>
                      <TableHead className="text-right">Isi/Karton</TableHead>
                      <TableHead className="text-right">Stok</TableHead>
                      <TableHead className="text-right">Beli</TableHead>
                      <TableHead className="text-right">Jual</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.slice(0, 50).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-xs">{r.sku || "-"}</TableCell>
                        <TableCell>{r.unit}</TableCell>
                        <TableCell className="text-right font-mono-num">{r.pack_size}</TableCell>
                        <TableCell className="text-right font-mono-num">{r.stock}</TableCell>
                        <TableCell className="text-right font-mono-num">{formatIDR(r.purchase_price)}</TableCell>
                        <TableCell className="text-right font-mono-num">{formatIDR(r.retail_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {rows.length > 50 && <div className="text-xs text-slate-500 p-2 text-center bg-slate-50">+{rows.length - 50} baris lagi...</div>}
              </div>
            </div>
          )}

          {result && (
            <Card className="p-4 border border-emerald-200 bg-emerald-50">
              <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-5 h-5 text-emerald-700"/><b className="text-emerald-800">Impor Selesai</b></div>
              <div className="grid grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-slate-600">Baru</div><div className="font-mono-num text-lg font-bold text-emerald-700">{result.created}</div></div>
                <div><div className="text-xs text-slate-600">Update</div><div className="font-mono-num text-lg font-bold text-blue-700">{result.updated}</div></div>
                <div><div className="text-xs text-slate-600">Lewat</div><div className="font-mono-num text-lg font-bold text-slate-500">{result.skipped}</div></div>
                <div><div className="text-xs text-slate-600">Error</div><div className="font-mono-num text-lg font-bold text-red-600">{result.errors.length}</div></div>
              </div>
              {result.errors.length > 0 && (
                <div className="mt-3 text-xs text-red-700">
                  <AlertCircle className="w-3 h-3 inline mr-1"/>
                  {result.errors.slice(0,5).map((e,i)=><div key={i}>Baris {e.row}: {e.reason}</div>)}
                </div>
              )}
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>Tutup</Button>
          <Button onClick={submit} disabled={rows.length === 0 || busy} data-testid="btn-submit-import" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            {busy ? "Mengimpor..." : `Impor ${rows.length} Produk`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
